import { Action, createAction, props } from "@ngrx/store";
import { ActionCreator, Creator } from "@ngrx/store/src/models";

import { SYNC_SUFFIX } from "../fetch-status/fetch-status.const";
import { ActionWithPayload } from "../model";

import {
	CACHED_ACTION_SUFFIX,
	CacheType,
	GetActionCached,
	StateCacheOptions,
	StateCacheStrategy,
	SUCCESS_SUFFIX,
	SuccessActionCache,
	SuccessActionPayload,
} from "./state-cache.model";

export function getCacheKey(fetchAction: Action | ActionWithPayload) {
	return "payload" in fetchAction ? fetchAction.type + JSON.stringify(fetchAction.payload) : fetchAction.type;
}

/**
 * Wraps get action to apply caching logic based on {@link StateCacheStrategy}
 *
 * If cache is hit, original `get` action will not be dispatched.
 * Uses {@link StateCacheStrategy.Indefinite} and action type as cache key by default.
 *
 * @example
 * createCachedGetAction(
 *		createAction(
 *			`${ACTION_PREFIX} Fetch All`,
 *			(provider?: string) => ({ provider })
 *		)
 * );
 *
 * All options can be provided either by the options parameter or through fetchAction. If both present, the values in fetchAction take
 * precedence.
 *
 * @example
 * createCachedGetAction(
 *		createAction(
 *			`${ACTION_PREFIX} Fetch All`,
 *			(provider?: string, cacheKey?: string) => ({ provider, cacheKey })
 *		)
 * );
 *
 * createCachedGetAction(
 *		createAction(
 *			`${ACTION_PREFIX} Fetch All Other`,
 *			(provider?: string) => ({ provider })
 *		), { cacheKey: `${CONTENT_SECTION_ACTION_TYPE.get}` }
 * );
 */
export function createCachedGetAction<T extends ActionCreator>(
	fetchActionCreator: T,
	options?: Partial<StateCacheOptions>
): T;

/**
 * @deprecated use the `ActionCreator` overload instead.
 *
 * Wraps get action to apply caching logic based on {@link StateCacheStrategy}
 *
 * If cache is hit, original `get` action will not be dispatched.
 * Uses {@link StateCacheStrategy.Indefinite} and action type as cache key by default.
 *
 * @example
 * getAll(): Action {
 *		return createCachedGetAction({
 *			type: COUNTRY_ACTION_TYPE.getAll
 *		});
 *	}
 */
export function createCachedGetAction(
	fetchAction: Action | ActionWithPayload,
	options?: Partial<StateCacheOptions>
): GetActionCached;

export function createCachedGetAction<T extends ActionCreator>(
	fetchActionOrCreator: (Action | ActionWithPayload) | T,
	options?: Partial<StateCacheOptions>
): GetActionCached | T {
	if (fetchActionOrCreator instanceof Function) {
		return createCachedGetActionNew(fetchActionOrCreator as T, options);
	}

	return createCachedGetActionOld(fetchActionOrCreator as Action, options);
}

function createCachedGetActionOld(
	fetchAction: Action | ActionWithPayload,
	options?: Partial<StateCacheOptions>
): GetActionCached {
	const payload = (fetchAction as ActionWithPayload).payload;
	const { cacheKey, ...newOptions } = options || {};

	const fetchActionCreator = createAction(fetchAction.type, props<{ payload: unknown; cacheKey?: string }>());

	// since createCachedGetActionNew return type supposed to be T, id doesn't match
	const cachedGetAction = {
		cacheStrategy: StateCacheStrategy.Indefinite,
		fetchAction: fetchActionCreator({ payload, cacheKey }),
		cacheKey: fetchActionCreator.type,
		...createCachedGetActionNew(fetchActionCreator, newOptions)({ payload, cacheKey }),
	} as unknown as CacheType & GetActionCached;
	cachedGetAction.cacheKey = cachedGetAction.cacheDetail.cacheKey;
	cachedGetAction.cacheStrategy = cachedGetAction.cacheDetail.cacheStrategy;

	return cachedGetAction;
}

// we revert back the action to ReturnType<T> before the consumer effect receives it
function createCachedGetActionNew<T extends ActionCreator>(
	fetchActionCreator: T,
	options?: Partial<StateCacheOptions>
): CacheType & T {
	const cachedActionType = `${fetchActionCreator.type} ${CACHED_ACTION_SUFFIX} ${SYNC_SUFFIX}`;

	// note: here's the actual return type
	const creator = (...args: any[]): ReturnType<T> & CacheType => {
		const fetchAction: any & Action = fetchActionCreator(...args);
		const {
			cacheKey: fetchActionCacheKey,
			cacheStrategy: fetchActionCacheStrategy,
			expirationMs: fetchActionExpirationMs,
			fallbackAction: fetchActionFallbackAction,
		} = fetchAction;

		return {
			...fetchAction,
			type: cachedActionType,
			cacheDetail: {
				originalType: fetchActionCreator.type,
				cacheKey: fetchActionCacheKey ?? options?.cacheKey ?? fetchActionCreator.type,
				cacheStrategy: fetchActionCacheStrategy ?? options?.cacheStrategy ?? StateCacheStrategy.Indefinite,
				expirationMs: fetchActionExpirationMs ?? options?.expirationMs,
				fallbackAction: fetchActionFallbackAction ?? options?.fallbackAction,
			},
		};
	};

	// sets original type on returned creator, so it can be used directly in effects
	// e.g. `ofType(cachedGet)` will catch original action, not cache wrapper
	return Object.defineProperty(creator, "type", {
		value: fetchActionCreator.type,
		writable: false,
	}) as unknown as CacheType & T;
}

/**
 *
 * Wraps get success action to track "lastFetched" status, which is used for applying cache logic to `get` action.
 * This is required for state cache to work, as `get` cache will only kick in after wrapped `getSuccess` is dispatched.
 *
 * @example
 * export const fetchAllSuccess = createGetSuccessAction(
 *		createAction(
 *			`${ACTION_PREFIX} Fetch All Success`,
 *			(games: Record<string, Game>) => ({ games })
 *		)
 *	);
 *
 * If `cacheKey` wants to be used it has to be included as parameter on the Action itself
 *
 * cacheKey: must match with provided in {@link createCachedGetAction}; if not provided action type without " Success"
 * suffix will be used
 *
 * @example
 * export const fetchAllSuccess = createGetSuccessAction(
 *		createAction(
 *			`${ACTION_PREFIX} Fetch All Success`,
 *			(games: Record<string, Game>, cacheKey: string) => ({ games, cacheKey })
 *		)
 *	);
 *
 */
export function createGetSuccessAction<T extends ActionCreator>(
	action: T
): ActionCreator<string, (...args: any) => SuccessActionCache & any>;

/**
 * @deprecated use the `ActionCreator` overload instead.
 *
 * Wraps get success action to track "lastFetched" status, which is used for applying cache logic to `get` action.
 * This is required for state cache to work, as `get` cache will only kick in after wrapped `getSuccess` is dispatched.
 *
 * @param cacheKey: must match with provided in {@link createCachedGetAction}; if not provided action type without " Success"
 * suffix will be used
 *
 * @example
 * getAllSuccess(response: GetCountriesResponse): ActionWithPayload {
 *		return createGetSuccessAction({
 *			type: COUNTRY_ACTION_TYPE.getAllSuccess,
 *			payload: response
 *		});
 *	}
 */
export function createGetSuccessAction(
	successAction: Action | ActionWithPayload,
	cacheKey?: string
): ActionWithPayload<SuccessActionPayload>;

export function createGetSuccessAction(
	successActionOrCreator: (Action | ActionWithPayload) | ActionCreator,
	cacheKey?: string
): ActionWithPayload<SuccessActionPayload> | ActionCreator {
	if (successActionOrCreator instanceof Function) {
		return createGetSuccessActionNew(successActionOrCreator as ActionCreator);
	}
	return createGetSuccessActionOld(successActionOrCreator, cacheKey);
}

function createGetSuccessActionOld(
	successAction: Action | ActionWithPayload,
	cacheKey?: string
): ActionWithPayload<SuccessActionPayload> {
	const payload = (successAction as ActionWithPayload).payload || {};

	const successActionCreator = createAction(successAction.type, props<{ payload: any; cacheKey?: string }>());

	return createGetSuccessActionNew(successActionCreator)({ payload, cacheKey });
}

function createGetSuccessActionNew<T extends ActionCreator>(
	successActionCreator: T
): ActionCreator<string, Creator<any[], SuccessActionCache & ReturnType<T>>> {
	const type = successActionCreator.type;

	return createAction(type, (...args: any[]) => {
		const successAction = successActionCreator(...args) as any;
		return {
			...successAction,
			...{
				isCachedSuccess: true,
				cacheKey: successAction.cacheKey ?? type.replace(SUCCESS_SUFFIX, ""),
			},
		};
	});
}

export function isCachedGet<T extends CacheType & Action>(action: T): action is T {
	return action.type.includes(CACHED_ACTION_SUFFIX);
}

export function isCachedGetSuccess<T extends SuccessActionCache>(action: T): action is T {
	return action && !!action.isCachedSuccess;
}

export function restoreOriginalAction(originalAction: Action, originalType: string) {
	return (originalAction as GetActionCached).fetchAction ?? { ...originalAction, type: originalType };
}
